import React from "react";
import { formatDate } from "../../utils/date";

export default function MobileMaterialViewContent({ data }) {
  const formType = (data?.form_type || "").toUpperCase();
  const header = data?.header || data?.meta?.header || {};
  const items = data?.items || data?.lines || [];
  const grandTotal = data?.total || header?.total || 0;

  const fmtNum = (v) =>
    v === undefined || v === null || v === "" ? "-" : Number(v).toLocaleString();

  const Field = ({ label, value, colSpan = 1, className = "" }) => (
    <div className={`${colSpan > 1 ? `col-span-${colSpan}` : ""} ${className}`}>
      <div className="text-xs text-gray-500 mb-1">{label}</div>
      <div className="text-sm text-gray-900 break-words whitespace-pre-wrap">
        {value || "-"}
      </div>
    </div>
  );

  const Section = ({ title, children, className = "" }) => (
    <div className={`bg-white rounded-lg border min-w-0 ${className}`}>
      {title && (
        <div className="bg-gray-50 px-3 py-2 border-b">
          <div className="text-sm font-medium text-gray-900">{title}</div>
        </div>
      )}
      <div className="p-3 space-y-3">{children}</div>
    </div>
  );

  // --------------------------------------------------------
  // 1. 헤더 (Form Type별 분기)
  // --------------------------------------------------------
  const renderHeader = () => {
    switch (formType) {
      case "PS": // 수급계획
        return (
          <Section title="기본 정보">
            <div className="grid grid-cols-2 gap-3">
              <Field label="공종" value={header.division} />
              <Field label="종류" value={formType} />
              <Field label="위치" value={header.area} />
              <Field label="층" value={header.level} />
              <Field label="좌표" value={header.grid} colSpan={2} />
              <Field
                label="요청일"
                value={formatDate(header.request_date)}
              />
              <Field
                label="시행일"
                value={formatDate(header.action_date)}
              />
              <Field
                label="내용"
                value={header.description}
                colSpan={2}
              />
              {header.relatedDrawing?.name && (
                <div className="col-span-2">
                  <div className="text-xs text-gray-500 mb-1">
                    관련 도면/시방서
                  </div>
                  <a
                    href={header.relatedDrawing.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 underline break-all"
                  >
                    {header.relatedDrawing.name}
                  </a>
                </div>
              )}
            </div>
          </Section>
        );

      case "PO": // 발주서
        return (
          <Section title="발주 정보">
            <div className="grid grid-cols-2 gap-3">
              <Field label="발주번호" value={header.order_no} colSpan={2} />
              <Field label="발주일" value={formatDate(header.po_date)} />
              <Field label="거래처" value={header.supplier} />
              <Field
                label="납기요청일"
                value={formatDate(header.request_date)}
              />
              <Field label="납품장소" value={header.delivery_place} />
              <Field label="메모" value={header.note} colSpan={2} />
            </div>
          </Section>
        );

      case "GR": // 입고서
      case "GI": // 출고서
        return (
          <Section title={formType === "GR" ? "입고 정보" : "출고 정보"}>
            <div className="grid grid-cols-2 gap-3">
              <Field
                label={formType === "GR" ? "입고일" : "출고일"}
                value={
                  formatDate(header.in_date || header.issue_date)
                }
              />
              <Field
                label={formType === "GR" ? "공급업체" : "출고처"}
                value={header.supplier || header.to_site}
              />
              {formType === "GI" && (
                <Field label="창고" value={header.warehouse} />
              )}
              {formType === "GI" && (
                <Field label="사유" value={header.reason} />
              )}
              <Field label="비고" value={header.note} colSpan={2} />
            </div>
          </Section>
        );

      case "QC": // 검수요청서
      case "CK": // 검수확인서
        return (
          <Section title="검수 정보">
            <div className="grid grid-cols-2 gap-3">
              <Field label="공종" value={header.division} />
              <Field label="위치" value={header.location} />
              <Field
                label="검수요청일"
                value={formatDate(header.request_date)}
              />
              <Field
                label="검수일"
                value={formatDate(header.check_date)}
              />
              {formType === "CK" && (
                <Field label="처리결과" value={header.result} />
              )}
              <Field label="비고" value={header.note} colSpan={2} />
            </div>
          </Section>
        );

      default:
        // 기타/공통
        return (
          <Section title="기본 정보">
            <div className="grid grid-cols-2 gap-3">
              <Field label="제목" value={data.title || header.title} colSpan={2} />
              <Field label="작성일" value={formatDate(data.created_at)} />
              <Field label="작성자" value={data.author_name} />
            </div>
          </Section>
        );
    }
  };

  // --------------------------------------------------------
  // 2. 아이템 목록 (Card List)
  // --------------------------------------------------------
  const renderItems = () => {
    // PS는 아이템 없음
    if (formType === "PS") return null;

    if (!items || items.length === 0) {
      return (
        <Section title="품목 목록">
          <div className="text-center text-gray-400 py-4 text-sm">
            품목이 없습니다.
          </div>
        </Section>
      );
    }

    return (
      <div className="space-y-3">
        <div className="flex justify-between items-center px-1">
          <h3 className="text-sm font-medium text-gray-900">품목 목록 ({items.length})</h3>
          {/* 합계 표시 (PO나 GR 등 금액이 있는 경우) */}
          {["PO", "GR", "GI"].includes(formType) && (
            <span className="text-xs font-bold text-blue-600">
              Total: {fmtNum(grandTotal)}
            </span>
          )}
        </div>

        {items.map((row, idx) => (
          <div key={idx} className="bg-white rounded-lg border p-3 shadow-sm">
            {/* 1행: 품명 (강조) + 상태/코드 */}
            <div className="flex justify-between items-start mb-1">
              <div className="font-medium text-sm text-gray-900 break-words pr-2">
                {row.name || "품명 없음"}
              </div>
              {row.code && (
                <div className="text-xs text-gray-400 flex-shrink-0">
                  {row.code}
                </div>
              )}
            </div>

            {/* 2행: 규격 | 단위 */}
            <div className="text-xs text-gray-500 mb-2">
              {row.spec || "-"}
              {row.unit && <span className="ml-1"> | {row.unit}</span>}
            </div>

            {/* 3행: 수량 / 단가 / 금액 (타입별 노출 다름) */}
            <div className="bg-gray-50 rounded p-2 text-xs flex justify-between items-center">
              <div className="flex gap-3">
                <div>
                  <span className="text-gray-400 mr-1">수량</span>
                  <span className="font-semibold">{fmtNum(row.qty)}</span>
                </div>
                {["PO", "GR", "GI"].includes(formType) && (
                  <div>
                    <span className="text-gray-400 mr-1">단가</span>
                    <span>{fmtNum(row.unit_price || row.price)}</span>
                  </div>
                )}
              </div>
              {["PO", "GR", "GI"].includes(formType) && (
                <div className="font-bold text-gray-800">
                  {fmtNum(row.amount || row.supply || row.total)}
                </div>
              )}
            </div>

            {/* 비고 */}
            {row.remark && (
              <div className="mt-2 text-xs text-gray-500 border-t pt-1">
                {row.remark}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-4 pb-4">
      {/* 타이틀 명시 (옵션) */}
      {/* <div className="text-lg font-bold px-1">{data.title}</div> */}

      {renderHeader()}
      {renderItems()}
    </div>
  );
}
